"use client";

import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import { Translate, X } from "@phosphor-icons/react/dist/ssr";
import { setLocaleClient, isLocale, readLocaleCookieClient, STORAGE_KEY, type Locale } from "@/lib/locale";
import { routing, stripLocalePrefix } from "@/i18n/routing";
import { acquireAppFrame, isAppFrameMounted, releaseAppFrame, useAppFrameMounted } from "@/lib/app-frame-store";
import { acquireLocaleSuggestion, releaseLocaleSuggestion } from "@/lib/locale-suggestion-store";

/**
 * "This page is also available in <language>" — a dismissible suggestion for a
 * visitor whose BROWSER language doesn't match the page they landed on.
 *
 * Why a suggestion and not a redirect: `localeDetection` is deliberately OFF
 * (see `i18n/routing.ts`) because auto-redirecting on `Accept-Language` / the
 * NEXT_LOCALE cookie hijacked in-page navigation and made shared EN links
 * preview with a VI OG card. This restores browser-language awareness WITHOUT
 * reintroducing either bug: it is client-only, so scrapers and crawlers never
 * see it, it never redirects, and it doesn't touch SSR or the edge cache.
 *
 * Shown BEFORE the cookie banner (which suppresses itself via
 * `locale-suggestion-store`) so consent is given in the visitor's own language.
 *
 * Storage: none of its own. It shows only while the visitor has NO explicit
 * language preference (`et_lang`), and both actions record one — switching
 * writes the target locale, dismissing writes the current one. So using the
 * LanguageSwitcher even once means this never appears again.
 */

// Copy is intentionally NOT in the i18n message files. It always renders in the
// TARGET language (that's the point — a Vietnamese speaker must be able to read
// the Vietnamese offer while sitting on an English page), whereas `useTranslations`
// resolves to the CURRENT page's locale. Do not "fix" this by moving it into
// en.json/vi.json — a translator localising these would break them.
const COPY = {
  vi: {
    message: "Trang này cũng có bằng Tiếng Việt.",
    cta: "Xem bằng Tiếng Việt",
    dismiss: "Đóng",
    region: "Gợi ý ngôn ngữ",
  },
  en: {
    message: "This page is also available in English.",
    cta: "View in English",
    dismiss: "Dismiss",
    region: "Language suggestion",
  },
  fr: {
    message: "Cette page est aussi disponible en français.",
    cta: "Voir en français",
    dismiss: "Fermer",
    region: "Suggestion de langue",
  },
  "zh-Hans": {
    message: "本页面也提供简体中文版本。",
    cta: "查看简体中文版",
    dismiss: "关闭",
    region: "语言建议",
  },
  "zh-Hant": {
    message: "本頁面也提供繁體中文版本。",
    cta: "查看繁體中文版",
    dismiss: "關閉",
    region: "語言建議",
  },
} as const;

/** Locales we suggest: exactly those with COPY. Add a locale's COPY only
 *  when its message file is full, or the offer moves the reader to English
 *  under a noindexed URL. */
type Suggestable = keyof typeof COPY;

// Once per page-session. Client-side navigation keeps this set, so ignoring the
// strip and clicking through the site doesn't re-nag — and, more importantly,
// can't hold the cookie banner back beyond that first screen. A hard reload
// resets it, which is the rare case and self-correcting.
let shownThisSession = false;
// The offer made this session, while it is still unanswered. Lets the copy
// that owns the screen take over an offer the other copy claimed: on the auth
// pages the inline copy mounts AFTER the fixed one has already run (it arrives
// in a later streamed chunk, not children-first), so without the hand-off the
// fixed copy claimed the session, hid itself for the inline one, and nobody
// showed the offer (measured 2026-09-26, /fr/login with an English browser).
let sessionTarget: Suggestable | null = null;

/** The offer for this page-session: made once, then handed to whichever copy
 *  owns the screen until it is answered or times out (`sessionTarget`). */
function sessionOffer(locale: string): Suggestable | null {
  if (shownThisSession) return sessionTarget;
  if (hasStoredLocalePreference()) return null; // they've already chosen a language
  const desired = browserPreference();
  if (desired === locale) return null; // already reading it in their language
  shownThisSession = true;
  sessionTarget = desired;
  return desired;
}

/** Long enough to read and act on, short enough that an ignored strip doesn't
 *  stall the cookie prompt for the rest of the visit. */
const AUTO_DISMISS_MS = 15_000;

/**
 * Internal / authenticated surfaces get no language offer. `/admin` is
 * deliberately English-only (see CLAUDE.md i18n), so switching there would land
 * on `/vi/admin` and still render English — a no-op offer. The dashboards ARE
 * localized, but they carry the LanguageSwitcher in their own nav, so the
 * suggestion is redundant noise over authenticated tooling. Same exclusion set
 * as the analytics `before_send` filter in PostHogProvider.
 */
// ⭐ `chat` LEFT this list 2026-09-05 (owner): a visitor who lands on the
// assistant speaking Vietnamese should be offered Vietnamese like anywhere
// else. It was excluded for a LAYOUT reason, never an audience one — a FIXED
// strip cannot sit correctly on an app frame — and that is now solved by
// rendering there in flow instead (`variant="inline"`, mounted by the page
// itself; see lib/app-frame-store). The rest of the list is unchanged and IS
// about audience: /admin is English-only, and the dashboards carry their own
// LanguageSwitcher.
const INTERNAL_ROUTE_RE = /^\/(admin|dashboard|pro|studio)(\/|$)/;

function hasStoredLocalePreference(): boolean {
  if (readLocaleCookieClient()) return true;
  try {
    return isLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private mode / storage disabled — treat as "no preference".
    return false;
  }
}

/** The visitor's PRIMARY browser language, narrowed to what we can offer. */
function browserPreference(): Suggestable {
  const first = navigator.languages?.[0] ?? navigator.language ?? "";
  // "vi", "vi-VN" → Vietnamese; "fr", "fr-CA" → French. Chinese goes by
  // SCRIPT: zh-Hant, and the regions that write Traditional (Taiwan, Hong
  // Kong, Macau), → Traditional; every other zh (zh, zh-CN, zh-SG, zh-Hans)
  // → Simplified. Everything else (ko, ja …) → English, the default.
  if (/^vi\b/i.test(first)) return "vi";
  if (/^fr\b/i.test(first)) return "fr";
  if (/^zh-(?:hant|tw|hk|mo)\b/i.test(first)) return "zh-Hant";
  if (/^zh\b/i.test(first)) return "zh-Hans";
  return "en";
}

interface LocaleSuggestionProps {
  /** `fixed` (default) floats under the Nav; `inline` renders as an ordinary
   *  flex child, for an app frame that has no document scroll. */
  variant?: "fixed" | "inline";
}

export default function LocaleSuggestion({ variant = "fixed" }: LocaleSuggestionProps) {
  // URL locale from context — static-safe, unlike usePathname()/useSearchParams(),
  // which would make the prerendered shell dynamic on [slug] routes.
  const locale = useLocale();
  const [target, setTarget] = useState<Suggestable | null>(null);
  // See CookieConsent for the contract; app-frame-store for why it is a store
  // and not a route test.
  const appFrameMounted = useAppFrameMounted();
  const inline = variant === "inline";
  useEffect(() => {
    if (!inline) return;
    acquireAppFrame();
    return releaseAppFrame;
  }, [inline]);

  useEffect(() => {
    // Only a locale we can actually WRITE the offer in. Derived from COPY
    // rather than listed, because the two had to agree and nothing said so: the
    // hand-written `locale !== "en" && locale !== "vi"` was already silently
    // gating /zh-Hans and /zh-Hant too, under a comment that only mentioned
    // /fr. Add a language to COPY and it becomes offerable here, once.
    if (!(locale in COPY)) return;
    // window.location (not usePathname) so this stays static-render-safe.
    if (INTERNAL_ROUTE_RE.test(stripLocalePrefix(window.location.pathname))) return;
    // Exactly one instance may claim an app frame: the inline one the page
    // mounts. ⛔ Read the store LIVE — `appFrameMounted` here is the value from
    // the render that scheduled this effect, and effects run children-first, so
    // a parent's copy still sees `false` after the inline copy has acquired.
    // Gating on the snapshot let the FIXED copy claim /chat, set
    // `shownThisSession` and render over the frame (review, 2026-09-05).
    if (isAppFrameMounted() !== inline) return;
    const offer = sessionOffer(locale);
    if (offer) setTarget(offer);
  }, [locale, appFrameMounted, inline]);

  // Tell CookieConsent to stand down while this is up (and again on unmount).
  // Refcounted, and claimed ONLY while actually showing: a copy that is not on
  // screen must not write the flag at all, or it clobbers the copy that is.
  // A copy holding an offer it no longer shows (the fixed copy, once the auth
  // pages' inline copy has taken the offer over) must release it too.
  useEffect(() => {
    if (target === null || appFrameMounted !== inline) return;
    acquireLocaleSuggestion();
    return releaseLocaleSuggestion;
  }, [target, appFrameMounted, inline]);

  // Bounded wait: this component lives in the layout, so an ignored strip would
  // otherwise survive client-side navigation and hold the cookie prompt back
  // indefinitely. Retire it after a while so consent can be asked. No preference
  // is recorded — the visitor didn't choose anything, so a later visit may offer
  // again (`shownThisSession` still prevents a re-nag on this one).
  useEffect(() => {
    if (target === null) return;
    const id = setTimeout(() => { sessionTarget = null; setTarget(null); }, AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [target]);

  // Belt and braces with the effect gate above, and the symmetry that was
  // missing: CookieConsent has always gated here, LocaleSuggestion did not, and
  // that asymmetry is what let a stale snapshot reach the screen.
  if (appFrameMounted !== inline) return null;
  if (!target) return null;
  const copy = COPY[target];

  const switchLocale = () => {
    // Mirrors LanguageSwitcher: sync both locale cookies, then a full-page nav
    // to the locale-prefixed URL computed from window.location at click time
    // (no usePathname, so this stays static-render-safe).
    setLocaleClient(target);
    const stripped = stripLocalePrefix(window.location.pathname);
    const prefix = target === routing.defaultLocale ? "" : `/${target}`;
    const dest = `${prefix}${stripped}` || "/";
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- a full load is the point: the locale cookies must reach the server, and useRouter would make the static Nav shell dynamic.
    window.location.assign(`${dest}${window.location.search}${window.location.hash}`);
  };

  const dismiss = () => {
    // Staying put IS a language choice — record it so this never asks again,
    // and so the cookie banner (next) renders in the language they kept.
    if (isLocale(locale)) setLocaleClient(locale as Locale);
    sessionTarget = null;
    setTarget(null);
  };

  return (
    <div
      role="region"
      aria-label={copy.region}
      // Appears after hydration, so announce it — a bare role="region" isn't.
      aria-live="polite"
      lang={target}
      // TOP, under the Nav — see the same note in CookieConsent. It was
      // bottom-flush (and before that, offset upward to clear the mobile
      // bottom bar, which was deleted 2026-09-05); the bottom edge is now
      // being given to the mini chat bar. The inline variant takes no position
      // at all — it is a flex child of an app frame, which sizes it.
      style={variant === "fixed" ? { top: "var(--sticky-top, 0px)", transition: "top .3s ease" } : undefined}
      className={`${variant === "fixed" ? "fixed inset-x-0 z-sticky" : "shrink-0"} border-b border-gold/40 bg-[#FFF8E7]/90 px-21 py-13 backdrop-blur-md sm:px-34`}
    >
      <div className="mx-auto flex max-w-[1200px] items-center gap-13">
        <Translate aria-hidden="true" weight="bold" className="hidden h-21 w-21 shrink-0 text-gold-ink-soft sm:block" />
        <p className="min-w-0 flex-1 text-sm text-navy">{copy.message}</p>
        <button
          type="button"
          onClick={switchLocale}
          className="shrink-0 whitespace-nowrap rounded-control bg-navy px-21 py-8 text-xs font-semibold uppercase tracking-[0.04em] text-white transition-colors hover:bg-navy/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          {copy.cta}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={copy.dismiss}
          className="shrink-0 rounded-control p-[4px] text-gray-600 transition-colors hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          <X aria-hidden="true" weight="bold" className="h-16 w-16" />
        </button>
      </div>
    </div>
  );
}
